// src/pages/LibraryPage/FoodTab.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Grid,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CallMerge as CallMergeIcon } from '@mui/icons-material';
import { UniversalPagination } from '../../components/common/UniversalPagination';
import {
  foodService,
  FoodResponseDto,
  GetFoodsParams,
  FoodCategory,
  FoodSourceType,
  MergeFoodDto,
  MeasurementUnit,
} from '../../services/food.service';
import { Locale } from '../../services/workout.service'; // Assuming Locale is shared
import { useDebounce } from '../../hooks/useDebounce';
import { toastService } from '../../services/toast.service';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

export const FoodTab: React.FC = () => {
  const [foods, setFoods] = useState<FoodResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [categoryFilter, setCategoryFilter] = useState<FoodCategory | ''>('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<FoodSourceType>(FoodSourceType.ALL);

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [foodToEdit, setFoodToEdit] = useState<FoodResponseDto | null>(null);

  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [foodToDelete, setFoodToDelete] = useState<FoodResponseDto | null>(null);

  const [isMergeModalOpen, setMergeModalOpen] = useState(false);
  const [foodToMerge, setFoodToMerge] = useState<FoodResponseDto | null>(null);
  const [mergeTarget, setMergeTarget] = useState<FoodResponseDto | null>(null);
  const [systemFoods, setSystemFoods] = useState<FoodResponseDto[]>([]);
  const [systemFoodsLoading, setSystemFoodsLoading] = useState(false);

  const locales: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

  const [currentFoodNameI18n, setCurrentFoodNameI18n] = useState<Partial<Record<Locale, string>>>(() => {
    const initialNames: Partial<Record<Locale, string>> = {};
    locales.forEach(loc => {
      initialNames[loc] = '';
    });
    return initialNames;
  });

  const [currentFoodCategory, setCurrentFoodCategory] = useState<FoodCategory>('');
  const [currentRawMeasurementUnit, setCurrentRawMeasurementUnit] = useState<MeasurementUnit>(MeasurementUnit.GRAM);
  const [currentCaloriesPer100g, setCurrentCaloriesPer100g] = useState<number>(0);

  // Options state
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [measurementUnitOptions, setMeasurementUnitOptions] = useState<{ value: string; label: string }[]>([]);

  // Fetch options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const options = await foodService.getFoodOptions();
        setCategoryOptions(options.categories);
        setMeasurementUnitOptions(options.measurementUnits);

        // Set default category if not set
        if (!currentFoodCategory && options.categories.length > 0) {
          setCurrentFoodCategory(options.categories[0].value);
        }
      } catch (err) {
        console.error('Failed to fetch food options:', err);
        toastService.error('Не вдалося завантажити налаштування.');
      }
    };
    fetchOptions();
  }, []);

  const fetchFoods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetFoodsParams = {
        page,
        limit,
        search: debouncedSearchQuery || undefined,
        sortBy: 'name',
        sortOrder: 'ASC',
        category: categoryFilter || undefined,
        sourceType: sourceTypeFilter,
      };
      const response = await foodService.getFoods(params);
      setFoods(response.data);
      if (response.meta && response.meta.total && response.meta.limit) {
        setTotalPages(Math.ceil(response.meta.total / response.meta.limit));
      } else {
        setTotalPages(0);
      }
    } catch (err) {
      toastService.error('Не вдалося завантажити список продуктів.');
      setError('Не вдалося завантажити список продуктів.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchQuery, categoryFilter, sourceTypeFilter]);

  useEffect(() => {
    fetchFoods();
  }, [fetchFoods]);

  const fetchSystemFoods = useCallback(async (search: string) => {
    setSystemFoodsLoading(true);
    try {
      const response = await foodService.searchSystemFoods(search, 20);
      setSystemFoods(response);
    } catch (err) {
      toastService.error('Не вдалося завантажити системні продукти.');
    } finally {
      setSystemFoodsLoading(false);
    }
  }, []);

  const handlePageChange = (value: number) => setPage(value);
  const handleLimitChange = (value: number) => { setLimit(value); setPage(1); };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };
  const handleCategoryFilterChange = useCallback((event: SelectChangeEvent<FoodCategory | ''>) => {
    setCategoryFilter(event.target.value as FoodCategory | '');
    setPage(1);
  }, []);
  const handleSourceTypeFilterChange = useCallback((event: SelectChangeEvent<FoodSourceType>) => {
    setSourceTypeFilter(event.target.value as FoodSourceType);
    setPage(1);
  }, []);

  const handleOpenCreateModal = () => {
    const initialNames: Partial<Record<Locale, string>> = locales.reduce((acc, loc) => {
      acc[loc] = '';
      return acc;
    }, {} as Partial<Record<Locale, string>>);
    setCurrentFoodNameI18n(initialNames);
    setCurrentFoodCategory(categoryOptions.length > 0 ? categoryOptions[0].value : '');
    setCurrentRawMeasurementUnit(MeasurementUnit.GRAM);
    setCurrentCaloriesPer100g(0);
    setCreateModalOpen(true);
  };

  const prepareNameI18n = (nameI18n: { [key in Locale]?: string }) => {
    const cleanedNameI18n: Partial<Record<Locale, string>> = Object.entries(nameI18n)
      .filter(([, value]) => value && value.trim() !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key as Locale]: value }), {});

    if (!cleanedNameI18n.uk) {
      toastService.error('Назва продукту (uk) є обов\'язковою.');
      return null;
    }
    return cleanedNameI18n as Partial<Record<Locale, string>> & { uk: string };
  };

  const handleCreateFood = async () => {
    const nameI18n = prepareNameI18n(currentFoodNameI18n);
    if (!nameI18n) return;

    setLoading(true);
    try {
      await foodService.createFood({
        nameI18n,
        category: currentFoodCategory,
        measurementUnit: currentRawMeasurementUnit,
        caloriesPer100g: currentCaloriesPer100g,
      });
      toastService.success('Продукт успішно створено!');
      setCreateModalOpen(false);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка створення продукту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (food: FoodResponseDto) => {
    setLoading(true);
    setFoodToEdit(food);
    try {
      const editData = await foodService.getFoodForEdit(food.id);
      setCurrentFoodNameI18n(editData.nameI18n as Partial<Record<Locale, string>>);
      setCurrentFoodCategory(editData.category);
      setCurrentRawMeasurementUnit(editData.rawMeasurementUnit);
      setCurrentCaloriesPer100g(editData.caloriesPer100g || 0);
      setEditModalOpen(true);
    } catch (err) {
      toastService.error('Не вдалося завантажити дані для редагування.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFood = async () => {
    if (!foodToEdit) return;

    const nameI18n = prepareNameI18n(currentFoodNameI18n);
    if (!nameI18n) return;

    setLoading(true);
    try {
      await foodService.updateFood(foodToEdit.id, {
        nameI18n,
        category: currentFoodCategory,
        measurementUnit: currentRawMeasurementUnit,
        caloriesPer100g: currentCaloriesPer100g,
      });
      toastService.success('Продукт успішно оновлено!');
      setEditModalOpen(false);
      setFoodToEdit(null);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка оновлення продукту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (food: FoodResponseDto) => {
    setFoodToDelete(food);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteFood = async () => {
    if (!foodToDelete) return;
    setLoading(true);
    try {
      await foodService.deleteFood(foodToDelete.id);
      toastService.success(`Продукт "${foodToDelete.name}" успішно видалено.`);
      setConfirmDeleteOpen(false);
      setFoodToDelete(null);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка видалення продукту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMergeModal = (food: FoodResponseDto) => {
    setFoodToMerge(food);
    setMergeTarget(null);
    setSystemFoods([]);
    setMergeModalOpen(true);
  };

  const handleMergeFood = async () => {
    if (!foodToMerge || !mergeTarget) return;
    setLoading(true);
    try {
      const mergeDto: MergeFoodDto = {
        sourceFoodId: foodToMerge.id,
        targetFoodId: mergeTarget.id,
      };
      const result = await foodService.mergeFoods(mergeDto);
      toastService.success(`Продукт '${result.removedFood.name}' об'єднано з '${result.targetFood.name}'. Оновлено подій: ${result.updatedCalendarEvents}.`);
      setMergeModalOpen(false);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка злиття: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const foodSourceTypes: FoodSourceType[] = [FoodSourceType.ALL, FoodSourceType.SYSTEM, FoodSourceType.USER];

  // Helper to get label for category
  const getCategoryLabel = (value: string) => {
    const option = categoryOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  // Helper to get label for measurement unit
  const getMeasurementUnitLabel = (value: string) => {
    const option = measurementUnitOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>Додати продукт</Button>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth label="Пошук за назвою" value={searchQuery} onChange={handleSearchChange} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} /></Grid>
          <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>Категорія</InputLabel><Select value={categoryFilter} label="Категорія" onChange={handleCategoryFilterChange}><MenuItem value=""><em>Всі</em></MenuItem>{categoryOptions.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>Джерело</InputLabel><Select value={sourceTypeFilter} label="Джерело" onChange={handleSourceTypeFilterChange}>{foodSourceTypes.map(t => <MenuItem key={t} value={t}>{t === FoodSourceType.ALL ? 'Всі' : t === FoodSourceType.SYSTEM ? 'Система' : 'Користувач'}</MenuItem>)}</Select></FormControl></Grid>
        </Grid>

        {error ? <Typography color="error" textAlign="center" sx={{ mt: 4 }}>{error}</Typography> : loading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box> : foods.length === 0 ? <Typography sx={{ mt: 4, textAlign: 'center' }}>Продуктів не знайдено.</Typography> : (
          <>
            <TableContainer><Table stickyHeader><TableHead><TableRow><TableCell>Назва</TableCell><TableCell>Категорія</TableCell><TableCell>Одиниця виміру</TableCell><TableCell>Ккал/100г</TableCell><TableCell>Джерело</TableCell><TableCell align="right">Дії</TableCell></TableRow></TableHead><TableBody>{foods.map((food) => (<TableRow hover key={food.id}><TableCell>{food.name}</TableCell><TableCell>{getCategoryLabel(food.category)}</TableCell><TableCell>
              {getMeasurementUnitLabel(food.measurementUnit)}
            </TableCell><TableCell>{food.caloriesPer100g}</TableCell><TableCell>{food.createdByUserId ? 'Користувач' : 'Система'}</TableCell><TableCell align="right">{food.createdByUserId && <IconButton onClick={() => handleOpenMergeModal(food)} disabled={loading}><CallMergeIcon /></IconButton>}<IconButton onClick={() => handleOpenEditModal(food)} disabled={loading}><EditIcon /></IconButton><IconButton onClick={() => handleOpenDeleteConfirm(food)} disabled={loading}><DeleteIcon /></IconButton></TableCell></TableRow>))}</TableBody></Table></TableContainer>
            <UniversalPagination page={page} count={totalPages} limit={limit} onPageChange={handlePageChange} onLimitChange={handleLimitChange} storageKey="pagination:library-food" />
          </>
        )}
      </Paper>

      <Dialog open={isCreateModalOpen} onClose={() => setCreateModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Додати новий продукт</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{locales.map(lang => <TextField key={lang} margin="dense" label={`Назва продукту (${lang.toUpperCase()})`} type="text" fullWidth variant="outlined" value={currentFoodNameI18n[lang] || ''} onChange={e => setCurrentFoodNameI18n({ ...currentFoodNameI18n, [lang]: e.target.value })} required={lang === 'uk'} />)}<FormControl fullWidth margin="dense"><InputLabel>Категорія</InputLabel><Select value={currentFoodCategory} label="Категорія" onChange={e => setCurrentFoodCategory(e.target.value as FoodCategory)}>{categoryOptions.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl>
        <FormControl fullWidth margin="dense">
            <InputLabel>Одиниця виміру</InputLabel>
            <Select
              value={currentRawMeasurementUnit}
              label="Одиниця виміру"
              onChange={e => setCurrentRawMeasurementUnit(e.target.value as MeasurementUnit)}
            >
              {measurementUnitOptions.map(unit => (
                <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Ккал/100г" type="number" fullWidth variant="outlined" value={currentCaloriesPer100g} onChange={e => setCurrentCaloriesPer100g(parseFloat(e.target.value) || 0)} />
        </Box></DialogContent>
        <DialogActions><Button onClick={() => setCreateModalOpen(false)}>Скасувати</Button><Button onClick={handleCreateFood} disabled={loading} variant="contained">{loading ? <CircularProgress size={24} /> : 'Створити'}</Button></DialogActions>
      </Dialog>

      <Dialog open={isEditModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Редагувати продукт</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{locales.map(lang => <TextField key={lang} margin="dense" label={`Назва продукту (${lang.toUpperCase()})`} type="text" fullWidth variant="outlined" value={currentFoodNameI18n[lang] || ''} onChange={e => setCurrentFoodNameI18n({ ...currentFoodNameI18n, [lang]: e.target.value })} required={lang === 'uk'} />)}<FormControl fullWidth margin="dense"><InputLabel>Категорія</InputLabel><Select value={currentFoodCategory} label="Категорія" onChange={e => setCurrentFoodCategory(e.target.value as FoodCategory)}>{categoryOptions.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl>
        <FormControl fullWidth margin="dense">
            <InputLabel>Одиниця виміру</InputLabel>
            <Select
              value={currentRawMeasurementUnit}
              label="Одиниця виміру"
              onChange={e => setCurrentRawMeasurementUnit(e.target.value as MeasurementUnit)}
            >
              {measurementUnitOptions.map(unit => (
                <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Ккал/100г" type="number" fullWidth variant="outlined" value={currentCaloriesPer100g} onChange={e => setCurrentCaloriesPer100g(parseFloat(e.target.value) || 0)} />
        </Box></DialogContent>
        <DialogActions><Button onClick={() => setEditModalOpen(false)}>Скасувати</Button><Button onClick={handleUpdateFood} disabled={loading} variant="contained">{loading ? <CircularProgress size={24} /> : 'Зберегти зміни'}</Button></DialogActions>
      </Dialog>

      <ConfirmationDialog open={isConfirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} onConfirm={handleDeleteFood} title="Підтвердити видалення" message={`Ви впевнені, що хочете видалити продукт "${foodToDelete?.name}"?`} confirmText="Видалити" />

      <Dialog open={isMergeModalOpen} onClose={() => setMergeModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Об'єднати продукти</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Ви збираєтеся об'єднати продукт <strong>{foodToMerge?.name}</strong> з системним продуктом. Усі записи в календарі будуть оновлені, а цей продукт буде видалено.</Typography>
          <Autocomplete
            options={systemFoods}
            getOptionLabel={(option) => `${option.name} (${getCategoryLabel(option.category)})`}
            loading={systemFoodsLoading}
            value={mergeTarget}
            onChange={(_event, newValue) => setMergeTarget(newValue)}
            onInputChange={(_event, newInputValue) => fetchSystemFoods(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Пошук системного продукту"
                variant="outlined"
                InputProps={{ ...params.InputProps, endAdornment: <>{systemFoodsLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeModalOpen(false)}>Скасувати</Button>
          <Button onClick={handleMergeFood} disabled={!mergeTarget || loading} variant="contained">{loading ? <CircularProgress size={24} /> : "Об'єднати"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
